// ============================================================================
// AFB-0075 / PR 2 — the pure rules for authoring a packet-ready template.
//
// Separate from envelope-lib.ts for exactly the reason template-document.ts is:
// that module imports pdfjs-dist at load time, which needs DOMMatrix, so it
// cannot be imported by a node-environment test AT ALL. Everything in here is
// arithmetic over rows and layout entries, so it is testable — and the rules
// worth pinning belong in a tested file rather than inside a component.
//
// The rule these encode: a template may only join a packet when the machine
// knows (a) that it carries a document, (b) how many pages that document is —
// assembly offsets every later document's field pages by it — and (c) what
// fills every merge field. A merge field naming a binding that is absent or
// inactive is a RESOLUTION GAP to report, never a silent blank on a signed
// legal document.
// ============================================================================

import { templateHasDocument } from './template-document';

/**
 * merge  = the system prefills esign_fields.value before the envelope is sent.
 * signer = left blank for the recipient to fill in.
 *
 * These behave differently everywhere downstream and must stay
 * distinguishable, which is why the layout entry carries the class rather than
 * it being inferred from whether a binding happens to be set.
 */
export type TemplateFieldClass = 'merge' | 'signer';

/** A row of esign_field_bindings — the only values a merge field may name. */
export interface FieldBindingOption {
  binding_key: string;
  label: string;
  is_active: boolean;
  is_sensitive: boolean;
  notes: string | null;
}

/**
 * The tagging half of a field_layout entry.
 *
 * Every property is optional on READ on purpose: the three seeded templates and
 * every layout captured before this PR were written without them, and a NULL
 * layout entry must still parse rather than throwing on load.
 */
export interface LayoutEntryTagging {
  field_class?: TemplateFieldClass | null;
  binding?: string | null;
  role_key?: string | null;
  /**
   * RESTRICTED HR FIELD — SSN / DOB / bank account / routing (2026-08-20).
   * Set by auto-placement's name matching OR by hand on the tagging list, which
   * is the only way to catch a box the PDF named "undefined".
   */
  is_sensitive?: boolean | null;
}

/** The same four, resolved — what the rest of the code may rely on. */
export interface FieldTagging {
  field_class: TemplateFieldClass;
  binding: string | null;
  role_key: string | null;
  is_sensitive: boolean;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Resolve one entry's tagging.
 *
 * A MISSING field_class reads as 'signer'. That is the safe default in the one
 * direction that matters: an untagged field left for the recipient to fill is a
 * visible blank on the document, whereas one wrongly treated as a merge field
 * would be prefilled from whatever binding happened to be lying on the entry.
 * For the same reason a signer field's binding is dropped rather than kept —
 * the column comment is explicit that binding is NULL unless the class is
 * merge, so a stale binding must not survive a class change.
 *
 * RESTRICTED OUTRANKS THE CLASS. is_sensitive forces 'signer' and drops the
 * binding, whatever the entry says. That is the one place the rule is enforced
 * for every caller — the tagging list, the save path, the readiness check and
 * the packet blockers all come through here — so an entry that somehow claims
 * to be both restricted and prefilled is read as restricted rather than
 * reported, argued about, or half-honoured. "We prefill the employee's SSN" is
 * not a state this system is willing to represent.
 */
export function normaliseFieldTagging(
  entry: LayoutEntryTagging | null | undefined,
): FieldTagging {
  const is_sensitive = entry?.is_sensitive === true;
  const field_class: TemplateFieldClass =
    !is_sensitive && entry?.field_class === 'merge' ? 'merge' : 'signer';
  const binding = blankToNull(entry?.binding);
  return {
    field_class,
    binding: field_class === 'merge' ? binding : null,
    role_key: blankToNull(entry?.role_key),
    is_sensitive,
  };
}

/**
 * Tick or untick "Restricted" on one layout entry, resolved.
 *
 * The whole reason this exists rather than the component setting a boolean:
 * ticking has TWO other consequences, and a UI that applied only the flag would
 * leave an entry that says "restricted" next to a binding that says "prefill
 * this from the employee record". Ticking forces the class to signer and clears
 * the binding, here, so the invalid state is never constructed in the first
 * place — as opposed to being constructed and then refused on save.
 *
 * Unticking is deliberately permitted and deliberately lossy: it leaves a plain
 * signer field with no binding. Name matching produces false positives ("Name
 * of Bank" is arguably just the bank's name), and Antonela is the one who knows
 * which. It does NOT restore a binding the tick cleared — if she wants the
 * field prefilled she chooses the value again, which is one deliberate act
 * rather than a value silently reappearing on a document somebody signs.
 */
export function markFieldRestricted(
  entry: LayoutEntryTagging | null | undefined,
  restricted: boolean,
): FieldTagging {
  return normaliseFieldTagging({ ...(entry ?? {}), is_sensitive: restricted });
}

/**
 * The bindings the authoring dropdown may offer.
 *
 * is_sensitive is filtered as well as is_active even though the table's
 * esign_field_bindings_no_active_sensitive CHECK already makes the pair
 * impossible: a UI that relies on a constraint it does not restate is one
 * migration away from offering somebody's SSN in a dropdown.
 */
export function selectableBindings(
  bindings: FieldBindingOption[],
): FieldBindingOption[] {
  return bindings.filter((b) => b.is_active && !b.is_sensitive);
}

/**
 * Why this layout entry is not packet-ready, or null when it is.
 *
 * Phrased to complete the sentence "Field 3 …", because the caller knows which
 * entry it is and this does not.
 */
export function fieldTaggingGap(
  entry: LayoutEntryTagging | null | undefined,
  bindings: FieldBindingOption[],
): string | null {
  const tagging = normaliseFieldTagging(entry);
  if (tagging.field_class !== 'merge') return null;
  if (!tagging.binding) {
    return 'is a merge field with no value chosen — tag it or make it a signer field';
  }
  const match = bindings.find((b) => b.binding_key === tagging.binding);
  if (!match) {
    return `pulls "${tagging.binding}", which is not a registered value`;
  }
  if (!match.is_active) {
    return `pulls "${tagging.binding}", which is no longer active`;
  }
  if (match.is_sensitive) {
    return `pulls "${tagging.binding}", which is marked sensitive and cannot be merged`;
  }
  return null;
}

/** Every entry in this layout that a packet could not resolve, with its index. */
export function untaggedMergeFields(
  layout: LayoutEntryTagging[] | null | undefined,
  bindings: FieldBindingOption[],
): Array<{ index: number; reason: string }> {
  if (!Array.isArray(layout)) return [];
  const out: Array<{ index: number; reason: string }> = [];
  layout.forEach((entry, index) => {
    const reason = fieldTaggingGap(entry, bindings);
    if (reason) out.push({ index, reason });
  });
  return out;
}

/** The parts of a template that decide whether it can be a packet member. */
export interface PacketMemberTemplate {
  name: string;
  document_bytes: number | null;
  page_count: number | null;
  field_layout: LayoutEntryTagging[] | null;
}

/**
 * Everything standing between this template and a packet, in plain sentences.
 * Empty array ⇒ ready.
 *
 * page_count is checked separately from the document because the two can only
 * disagree one way: the document RPC and the page-count RPC are two calls, so a
 * saved document with a null page count means the parse or the second call
 * failed. That template must NOT be assembled — every later document's fields
 * would land on the wrong page, on a document somebody then signs.
 */
export function templatePacketBlockers(
  template: PacketMemberTemplate,
  bindings: FieldBindingOption[],
): string[] {
  const blockers: string[] = [];
  if (!templateHasDocument(template)) {
    blockers.push('No document saved — attach the PDF above.');
  }
  if ((template.page_count ?? 0) <= 0) {
    blockers.push(
      'Page count unknown — re-save the document so the page count can be read from it. ' +
        'Packet assembly shifts every later document by this number.',
    );
  }
  const untagged = untaggedMergeFields(template.field_layout, bindings);
  for (const { index, reason } of untagged) {
    blockers.push(`Field ${index + 1} ${reason}.`);
  }
  return blockers;
}

/** True only when nothing at all is outstanding. */
export function templateIsPacketReady(
  template: PacketMemberTemplate,
  bindings: FieldBindingOption[],
): boolean {
  return templatePacketBlockers(template, bindings).length === 0;
}
